import { useQuery } from "@tanstack/react-query";

import { ProductData, type ProductId } from "@livechat/design-system-react-components";

import { QueryKeys } from "constants/query-keys";
import { useOrganization } from "hooks/use-organizations";
import { ApiManager } from "services/api";
import { ISubscriptionDataV3, ProductSubscriptionStatus } from "services/api/accounts-billing-v3/interfaces";

export const fetchMyProducts = async (): Promise<ProductData[]> => {
  const { result, error } = await ApiManager.globalAccountsApi.fetchMyProducts();

  if (error) {
    throw error;
  }

  return result;
};

export const fetchSubscriptions = async (): Promise<ISubscriptionDataV3> => {
  const { result, error } = await ApiManager.accountsBillingV3Api.fetchActivatedSubscriptions();

  if (error) {
    throw error;
  }

  return result;
};

interface IUseProductData {
  organizationId: string;
  installedProducts: ProductData[];
  subscriptions: Partial<Record<ProductId, { status: ProductSubscriptionStatus; next_charge_at: string }>>;
}

export const useProductData = (): IUseProductData => {
  const {
    organization: { organizationId },
  } = useOrganization();

  const { data: installedProducts } = useQuery([QueryKeys.MyProducts], fetchMyProducts, {
    refetchOnWindowFocus: false,
    initialData: [],
  });

  const {
    data: { subscriptions },
  } = useQuery([QueryKeys.ActivatedSubscriptions], fetchSubscriptions, {
    refetchOnWindowFocus: false,
    initialData: () => ({ subscriptions: {} }),
  });

  return {
    organizationId,
    installedProducts,
    subscriptions,
  };
};
